import { captureMessage } from '@sentry/remix'
import { useEffect } from 'react'
import type { MouseEvent } from 'react'
import { DangerousHtml } from '~/components/dangerousHtml/dangerousHtml'
import { StackableFusuma } from '~/components/modal/StackableFusuma'
import { useEnv } from '~/env'
import { useCloseNewReserveInput } from '~/features/reservationInput/shared/hooks'
import { forceRedirectToYahooLogin } from '~/lib/member/yahoo'
import { useIsPC } from '~/lib/ua'
import {
  type SerializedClientError,
  type SerializedRecoverableClientError,
  useReserveOverlay,
} from '~/navigation'
import { css } from '~/styled-system/css'
import { Button } from '~/ui'
import { YahooMemberRelationError } from './YahooMemberRelationError'

export function ReservationError() {
  const { error, back, willBeUnmounted } = useReservationError()
  const env = useEnv()
  const isPc = useIsPC()
  if (error === undefined) {
    return null
  }
  switch (error.type) {
    case 'RedirectError':
      // RedirectError の場合は、各値を元にリダイレクトする
      switch (error.content.type) {
        // Yahoo! 連携の refresh token が期限切れのときは、宿泊に合わせてログイン画面にリダイレクトさせる
        case 'MEMBER_OAUTH_YID':
          forceRedirectToYahooLogin(env)
          return null
        // YID の再認証が必要なときは、url へリダイレクトさせる
        case 'YAHOO_REAUTH_YID':
          location.href = error.content.url
          return null
      }
    // クライアントエラーの場合は、エラーふすまを表示する
    case 'ClientError':
      return isPc ? (
        <Body error={error} back={back} />
      ) : (
        <StackableFusuma title="ご予約情報の入力・確認" willBeUnmounted={willBeUnmounted}>
          <Body error={error} back={back} />
        </StackableFusuma>
      )
    case 'RecoverableClientError':
      return isPc ? (
        <Body error={error} back={back} />
      ) : (
        <StackableFusuma title="ご予約情報の入力・確認" willBeUnmounted={willBeUnmounted}>
          <Body error={error} back={back} />
        </StackableFusuma>
      )
    // それ以外のエラーの場合は、エラーを throw して ErrorBoundary に任せる
    case 'UnknownError':
      throw new Error(error.message)
  }
}

function useReservationError() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_ERROR')
  const error = context?.error
  const back = useCloseNewReserveInput()
  return { error, back, willBeUnmounted }
}

function Body({
  error,
  back,
}: {
  error: SerializedRecoverableClientError | SerializedClientError
  back: (e: MouseEvent<HTMLElement>) => void
}) {
  const isPc = useIsPC()
  // 標準の ErrorBoundary で拾っていないのでここで明示的に投げる
  useEffect(() => {
    if (error !== null && error !== undefined) {
      switch (error.content.type) {
        case 'html':
          captureMessage(error.content.fragments.map((fragment) => fragment).join(''), 'error')
          break
        case 'string':
          captureMessage(error.content.message, 'error')
          break
        // YID 会員連携エラーは想定されているエラーであり、
        // sentry で捕捉して何かアクションを起こさなければいけないエラーではないので何もしない
        case 'memberRelation':
          break
      }
    }
  }, [error])

  return (
    <div className="flex flex-col text-center">
      <div className="space-y-5 px-3 py-5 ">
        <div className="space-y-2 border border-solid border-accent-red p-4">
          <h2 className="text-xl font-bold">エラー</h2>
          {error.content.type === 'string' && (
            <p className="whitespace-pre-wrap text-accent-red">{error.content.message}</p>
          )}
          {error.content.type === 'html' &&
            error.content.fragments.map((fragment, index) => (
              <DangerousHtml
                className="whitespace-pre-wrap text-accent-red"
                fragment={fragment}
                key={index}
              />
            ))}
          {error.content.type === 'memberRelation' && (
            <div className={css({ color: 'error.text', whiteSpace: 'pre-wrap' })}>
              <YahooMemberRelationError error={error.content.errorType} />
            </div>
          )}
        </div>
        <Button color="tertiary" onClick={back} spread={true}>
          戻る
        </Button>
      </div>
      {!isPc && (
        <a href="/" className="block text-accent-blue">
          トップページへ戻る
        </a>
      )}
    </div>
  )
}
